const SHEETS_BASE = 'https://sheets.googleapis.com/v4/spreadsheets';

// 建立試算表（自訂分頁名稱）
export async function createSpreadsheet(accessToken, title, sheetNames, folderId = null) {
  const sheets = sheetNames.map((name, i) => ({ properties: { title: name, index: i } }));
  const res = await fetch(SHEETS_BASE, {
    method: 'POST',
    headers: { Authorization: `Bearer ${accessToken}`, 'Content-Type': 'application/json' },
    body: JSON.stringify({ properties: { title }, sheets }),
  });
  const data = await res.json();
  if (!res.ok) throw new Error(data.error?.message || '建立試算表失敗');
  if (folderId) await moveFileToDrive(accessToken, data.spreadsheetId, folderId);
  return data.spreadsheetId;
}

async function moveFileToDrive(accessToken, fileId, folderId) {
  const metaRes = await fetch(
    `https://www.googleapis.com/drive/v3/files/${fileId}?fields=parents`,
    { headers: { Authorization: `Bearer ${accessToken}` } }
  );
  const meta = await metaRes.json();
  const prevParents = (meta.parents || []).join(',');
  await fetch(
    `https://www.googleapis.com/drive/v3/files/${fileId}?addParents=${folderId}&removeParents=${prevParents}&fields=id`,
    { method: 'PATCH', headers: { Authorization: `Bearer ${accessToken}` } }
  );
}

async function addSheetTab(accessToken, spreadsheetId, sheetName) {
  const res = await fetch(`${SHEETS_BASE}/${spreadsheetId}:batchUpdate`, {
    method: 'POST',
    headers: { Authorization: `Bearer ${accessToken}`, 'Content-Type': 'application/json' },
    body: JSON.stringify({ requests: [{ addSheet: { properties: { title: sheetName } } }] }),
  });
  const data = await res.json();
  if (!res.ok && !data.error?.message?.includes('already exists')) {
    throw new Error(data.error?.message || `新增分頁「${sheetName}」失敗`);
  }
}

export async function writeSheet(accessToken, spreadsheetId, sheetName, rows) {
  const metaRes = await fetch(
    `${SHEETS_BASE}/${spreadsheetId}?fields=sheets.properties.title`,
    { headers: { Authorization: `Bearer ${accessToken}` } }
  );
  const meta = await metaRes.json();
  const existing = (meta.sheets || []).map(s => s.properties.title);
  if (!existing.includes(sheetName)) await addSheetTab(accessToken, spreadsheetId, sheetName);

  const writeRes = await fetch(
    `${SHEETS_BASE}/${spreadsheetId}/values/${encodeURIComponent(sheetName + '!A1')}?valueInputOption=USER_ENTERED`,
    {
      method: 'PUT',
      headers: { Authorization: `Bearer ${accessToken}`, 'Content-Type': 'application/json' },
      body: JSON.stringify({ values: rows }),
    }
  );
  if (!writeRes.ok) {
    const err = await writeRes.json();
    throw new Error(err.error?.message || `寫入「${sheetName}」失敗`);
  }
}

export async function readSheet(accessToken, spreadsheetId, sheetName) {
  const res = await fetch(
    `${SHEETS_BASE}/${spreadsheetId}/values/${encodeURIComponent(sheetName)}`,
    { headers: { Authorization: `Bearer ${accessToken}` } }
  );
  const data = await res.json();
  return data.values || [];
}

// 判斷是否為體驗課學員（名字包含「體驗課」或有 hasTrial 標記）
function isTrial(s) {
  return s.hasTrial || (s.name && s.name.includes('體驗'));
}

// ── 1. 學員總覽
// 順序：正式在籍 → 正式畢業（空行分隔） → 體驗課在籍 → 體驗課畢業（空行分隔）
export function formatStudentsSheet(students, coachColorMap, calendarCoachMap) {
  const header = ['姓名', '購買堂數', '已上堂數', '剩餘堂數', '單堂定價', '總繳費', '剩餘餘額', '狀態', '備註'];

  function makeRow(s) {
    const baseRate = Number(s.baseRate) || 0;
    // 只計算真實付款（排除代付虛擬紀錄）
    const realPayments = (s.payments || []).filter(p => !p.isProxy);
    const purchased = realPayments.reduce((a, p) => a + (Number(p.lessons) || 0), 0);
    const totalAmt  = realPayments.reduce((a, p) => a + (Number(p.amount)  || 0), 0);
    const used      = Number(s.used) || 0;
    const perLesson = purchased > 0 ? Math.round(totalAmt / purchased) : (baseRate || 0);

    if (baseRate > 0) {
      // 金額制：計算剩餘餘額
      const selfCost = used * baseRate;
      const proxyCost = (s.payments || []).reduce((sum, p) =>
        (p.isProxy && p.proxyType === 'payer') ? sum + (Number(p.proxyAmount) || 0) : sum, 0);
      const proxyReceived = (s.payments || []).reduce((sum, p) =>
        (p.isProxy && p.proxyType === 'beneficiary') ? sum + (Number(p.proxyAmount) || 0) : sum, 0);
      const balance = totalAmt + proxyReceived - selfCost - proxyCost;
      const estimatedLessons = Math.floor(balance / baseRate);
      return [
        s.name,
        purchased,
        used,
        estimatedLessons,
        baseRate,
        totalAmt,
        balance,
        s.graduated ? '已畢業' : '在籍',
        s.notes || '',
      ];
    }

    // 舊制（未設定 baseRate）
    return [
      s.name,
      purchased,
      used,
      purchased - used,
      perLesson > 0 ? perLesson : '',
      totalAmt,
      '',
      s.graduated ? '已畢業' : '在籍',
      s.notes || '',
    ];
  }

  const normalActive   = students.filter(s => !isTrial(s) && !s.graduated);
  const normalGrad     = students.filter(s => !isTrial(s) &&  s.graduated);
  const trialActive    = students.filter(s =>  isTrial(s) && !s.graduated);
  const trialGrad      = students.filter(s =>  isTrial(s) &&  s.graduated);

  const rows = [];
  normalActive.forEach(s => rows.push(makeRow(s)));
  if (normalGrad.length > 0) {
    rows.push(Array(header.length).fill(''));
    normalGrad.forEach(s => rows.push(makeRow(s)));
  }
  if (trialActive.length > 0 || trialGrad.length > 0) {
    rows.push(Array(header.length).fill(''));
    trialActive.forEach(s => rows.push(makeRow(s)));
    if (trialGrad.length > 0) {
      rows.push(Array(header.length).fill(''));
      trialGrad.forEach(s => rows.push(makeRow(s)));
    }
  }
  return [header, ...rows];
}

// ── 2. 上課紀錄
export function formatLessonsSheet(lessons, coachColorMap, calendarCoachMap) {
  const header = ['日期', '時間', '姓名', '授課教練', '備註'];
  const rows = [...lessons]
    .sort((a, b) => {
      const da = a.date ? new Date(a.date.replace(/\//g, '-')) : new Date(0);
      const db = b.date ? new Date(b.date.replace(/\//g, '-')) : new Date(0);
      return da - db;
    })
    .map(l => {
      const coachName =
        (calendarCoachMap && l.calendarId && calendarCoachMap[l.calendarId]) ||
        (coachColorMap && l.colorId && coachColorMap[l.colorId]) ||
        '';
      return [l.date, l.time || '', l.studentName, coachName, l.notes || ''];
    });
  return [header, ...rows];
}

// ── 3. 付款紀錄（正式學員在前，體驗課在後）
// 代付紀錄依 linkedId 配對：代付人那筆出現時，緊接著插入被代付人那筆
export function formatPaymentsSheet(students) {
  const header = ['日期', '姓名', '類型', '堂數', '金額', '單堂價格', '累計堂數', '累計金額'];

  // 建立 linkedId → 被代付紀錄的快速查找表（跨學員）
  // key: linkedId, value: { studentName, payment }
  const beneficiaryMap = new Map();
  students.forEach(s => {
    (s.payments || []).forEach(p => {
      if (p.isProxy && p.proxyType === 'beneficiary' && p.linkedId) {
        beneficiaryMap.set(p.linkedId, { studentName: s.name, payment: p });
      }
    });
  });

  function makeProxyRow(studentName, p, students) {
    const proxyAmt = Number(p.proxyAmount) || 0;
    const typeLabel = p.proxyType === 'payer' ? '代付' : '被代付';
    const displayAmt = p.proxyType === 'payer' ? -proxyAmt : proxyAmt;
    return [p.date || '', studentName, typeLabel, '', displayAmt, '', '', ''];
  }

  function studentRows(s) {
    const rows = [];
    const baseRate = Number(s.baseRate) || 0;
    // 只處理非 beneficiary 的紀錄（beneficiary 紀錄由代付人那筆帶出，緊接在後）
    const sorted = [...(s.payments || [])]
      .filter(p => !(p.isProxy && p.proxyType === 'beneficiary'))
      .sort((a, b) =>
        (a.date ? new Date(a.date) : new Date(0)) - (b.date ? new Date(b.date) : new Date(0))
      );
    let cumL = 0, cumA = 0;
    sorted.forEach(p => {
      if (p.isProxy && p.proxyType === 'payer') {
        // 代付人那筆
        rows.push(makeProxyRow(s.name, p));
        // 緊接著插入被代付人那筆（若找得到）
        if (p.linkedId && beneficiaryMap.has(p.linkedId)) {
          const { studentName, payment } = beneficiaryMap.get(p.linkedId);
          rows.push(makeProxyRow(studentName, payment));
        }
        return;
      }
      // 一般付款
      const lessons = Number(p.lessons) || 0;
      const amount  = Number(p.amount)  || 0;
      const effectiveLessons = baseRate > 0 ? Math.floor(amount / baseRate) : lessons;
      cumL += effectiveLessons;
      cumA += amount;
      const perLesson = baseRate > 0 ? baseRate : (lessons > 0 ? Math.round(amount / lessons) : 0);
      rows.push([
        p.date || '',
        s.name,
        '付款',
        effectiveLessons,
        amount,
        perLesson || '',
        cumL,
        cumA,
      ]);
    });
    return rows;
  }

  const normal = students.filter(s => !isTrial(s));
  const trial  = students.filter(s =>  isTrial(s));
  const rows = [];
  normal.forEach(s => rows.push(...studentRows(s)));
  if (trial.length > 0) {
    rows.push(Array(header.length).fill(''));
    trial.forEach(s => rows.push(...studentRows(s)));
  }
  return [header, ...rows];
}
